import { useReducer, useCallback, useEffect, useRef } from 'react';
import { Address } from 'types';
import {
  AddressInputState,
  AddressInputEvent,
  AddressInputData,
  ValidatedAddress,
} from './types';

interface UseAddressStateMachineOptions {
  onComplete?: (address: ValidatedAddress) => void;
  onValidate?: (address: Address) => Promise<ValidatedAddress>;
  initialAddress?: Address;
}

interface MachineState {
  state: AddressInputState;
  data: AddressInputData;
}

type MachineAction =
  | { type: 'VALIDATION_START'; currentAddress?: Address }
  | {
      type: 'VALIDATION_SUCCESS';
      validated: ValidatedAddress;
      originalAddress: Address;
    }
  | { type: 'VALIDATION_FAILURE'; error: string }
  | { type: 'ENTER_UNIT'; payload: string }
  | { type: 'CONTINUE' }
  | { type: 'RESET' };

function reducer(machine: MachineState, action: MachineAction): MachineState {
  switch (action.type) {
    case 'VALIDATION_START':
      return {
        state: 'validating',
        data: action.currentAddress
          ? { currentAddress: action.currentAddress }
          : {},
      };

    case 'VALIDATION_SUCCESS': {
      if (action.validated.validation?.requiresUnit) {
        return {
          state: 'needsUnit',
          data: {
            currentAddress: action.originalAddress,
            validationResult: action.validated.validation,
          },
        };
      }
      // INVALID addresses also land here (requiresUnit is false).
      // data.validationResult carries the errorMessage but nothing in the current UI renders it —
      // users can proceed unblocked with an unverified address.
      return {
        state: 'ready',
        data: {
          currentAddress: action.validated,
          validationResult: action.validated.validation,
        },
      };
    }

    case 'VALIDATION_FAILURE':
      return { state: 'empty', data: { error: action.error } };

    case 'ENTER_UNIT': {
      if (machine.state !== 'needsUnit' && machine.state !== 'ready')
        return machine;
      const unit = action.payload.trim();
      return {
        state: unit ? 'ready' : 'needsUnit',
        data: {
          ...machine.data,
          currentAddress: machine.data.currentAddress
            ? { ...machine.data.currentAddress, unit }
            : undefined,
        },
      };
    }

    case 'CONTINUE':
      if (machine.state === 'ready' && machine.data.currentAddress) {
        return { ...machine, state: 'complete' };
      }
      return machine;

    case 'RESET':
      return { state: 'empty', data: {} };

    default:
      return machine;
  }
}

/**
 * Finite State Machine for managing address input flow
 *
 * State transitions:
 * empty → validating → ready → complete
 */
export function useAddressStateMachine(options: UseAddressStateMachineOptions) {
  const { onComplete, onValidate, initialAddress } = options;

  const [machine, dispatch] = useReducer(reducer, { state: 'empty', data: {} });
  const hasInitialized = useRef(false);

  // Stable refs for values read inside event handlers
  // after a React 19 upgrade, can switch to useEffectEvent
  const machineRef = useRef(machine);
  machineRef.current = machine;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const runValidation = useCallback(
    async (address: Address, showDuringValidation = false) => {
      if (onValidate) {
        dispatch({
          type: 'VALIDATION_START',
          currentAddress: showDuringValidation ? address : undefined,
        });
        try {
          const validated = await onValidate(address);
          dispatch({
            type: 'VALIDATION_SUCCESS',
            validated,
            originalAddress: address,
          });
        } catch (error) {
          dispatch({
            type: 'VALIDATION_FAILURE',
            error: error instanceof Error ? error.message : 'Validation failed',
          });
        }
      } else {
        dispatch({
          type: 'VALIDATION_SUCCESS',
          validated: { ...address },
          originalAddress: address,
        });
      }
    },
    [onValidate]
  );

  useEffect(() => {
    if (
      initialAddress &&
      !hasInitialized.current &&
      isValidAddress(initialAddress)
    ) {
      hasInitialized.current = true;
      runValidation(initialAddress, true);
    }
  }, [initialAddress, runValidation]);

  const transition = useCallback(
    async (event: AddressInputEvent) => {
      switch (event.type) {
        case 'SELECT_ADDRESS':
          await runValidation(event.payload);
          break;
        case 'ENTER_UNIT':
          dispatch({ type: 'ENTER_UNIT', payload: event.payload });
          break;
        case 'CONTINUE': {
          const { state, data } = machineRef.current;
          if (state === 'ready' && data.currentAddress) {
            dispatch({ type: 'CONTINUE' });
            onCompleteRef.current?.(data.currentAddress as ValidatedAddress);
          }
          break;
        }
        case 'RESET':
          dispatch({ type: 'RESET' });
          break;
      }
    },
    [runValidation]
  );

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  return {
    state: machine.state,
    data: machine.data,
    transition,
    reset,
  };
}

/**
 * Helper to check if an address has all required fields
 */
function isValidAddress(address: Address): boolean {
  return !!(
    address.street &&
    address.city &&
    address.state &&
    address.postalCode
  );
}
